import React, { Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { SkeletonCardGrid } from "../../components/general/skeleton";
import LoginPageComponent from "../../pages/accounts/LoginPageComponent";
import RegisterPageComponent from "../../pages/accounts/RegisterPageComponent";
// prices
// items
import ItemListPageComponent from "../../pages/items/ItemListPageComponent";
import HomePageComponent from "../../pages/home/HomePageComponent";
import DialogManagerComponent from "../../components/dialogs/DialogManagerComponent";
import ShoppingCartPageComponent from "../../pages/shoppingCart/ShoppingCartPageComponent.jsx";
import AdminRoute from "../../middlewares/routing/AdminRoute.middleware.js";
import ThankYouPageComponent from "../../pages/orders/ThankYouPageComponent.jsx";
import BusinessRoute from "../../middlewares/routing/BusinessRoute.middleware.js";
import BusinessDashboardPageComponent from "../../pages/dashboard/BusinessDashboardPageComponent.jsx";
import OrdersHistoryPageComponent from "../../pages/dashboard/orders/OrdersHistoryPageComponent.jsx";
import BusinessOrdersTableComponent from "../../components/orders/BusinessOrdersTableComponent.jsx";
import AgentRoute from "../../middlewares/routing/AgentRoute.middleware.js";
// 0026: visual category gallery (separate additive base — does NOT touch /categories*)
import CategoryGalleryPageComponent from "../../pages/categories/CategoryGalleryPageComponent.jsx";


// 0035: code splitting. Before this the app shipped as ONE 402KB gzipped chunk, so an anonymous
// shopper downloaded the entire admin area, the agent area and the charting library before seeing
// a product. Everything a public visitor can reach stays eagerly imported above — a shopper should
// never wait on a chunk mid-journey. Everything behind a role guard is fetched on demand.

// Legacy management pages
const AddCategoryPageComponent = React.lazy(() => import("../../pages/items/categories/AddCategoryPageComponent"));
const CategoriesListPageComponent = React.lazy(() => import("../../pages/items/categories/CategoriesListPageComponent"));
const EditCategoryPageComponent = React.lazy(() => import("../../pages/items/categories/EditCategoryPageComponent"));
const AddAttributePageComponent = React.lazy(() => import("../../pages/items/attributes/AddAttributePageComponent"));
const GetAttributesPageComponent = React.lazy(() => import("../../pages/items/attributes/GetAttributesPageComponent"));
const EditAttributePageComponent = React.lazy(() => import("../../pages/items/attributes/EditAttributePageComponent"));
const AttributeValuesPageComponent = React.lazy(() => import("../../pages/items/attributes/attributesValues/AttributeValuesPageComponent"));
const AddPriceListPageComponent = React.lazy(() => import("../../pages/items/prices/AddPriceListPageComponent"));
const GetPriceListsPageComponent = React.lazy(() => import("../../pages/items/prices/GetPriceListsPageComponent"));
const EditPriceListPageComponent = React.lazy(() => import("../../pages/items/prices/EditPriceListPageComponent"));
const AddProductPageComponent = React.lazy(() => import("../../pages/items/controls/AddProductPageComponent"));
const EditProductPageComponent = React.lazy(() => import("../../pages/items/controls/EditProductPageComponent"));
const ManageProductPricePageComponent = React.lazy(() => import("../../pages/items/controls/ManageProductPricePageComponent"));
const ManageProductImagesPageComponent = React.lazy(() => import("../../pages/items/controls/ManageProductImagesPageComponent"));

// Admin area
const AdminDashboardPageComponent = React.lazy(() => import("../../pages/dashboard/admin/AdminDashboardPageComponent.jsx"));
const AdminManageItemPageComponent = React.lazy(() => import("../../pages/dashboard/admin/AdminManageItemPageComponent.jsx"));
const AdminDisplayOrderPageComponent = React.lazy(() => import("../../pages/dashboard/admin/AdminDisplayOrderPageComponent.jsx"));
const AdminCatalogManagementPageComponent = React.lazy(() => import("../../pages/dashboard/admin/AdminCatalogManagementPageComponent.jsx"));
const AdminAnalyticsPageComponent = React.lazy(() => import("../../pages/dashboard/admin/AdminAnalyticsPageComponent.jsx"));

// Agent area
const AgentDashboardPageComponent = React.lazy(() => import("../../pages/dashboard/agent/AgentDashboardPageComponent.jsx"));
const AgentAnalyticsPageComponent = React.lazy(() => import("../../pages/dashboard/agent/AgentAnalyticsPageComponent.jsx"));

// Shown while a lazy route's chunk is in flight. Reuses the app's own skeleton rather than
// introducing a spinner that appears nowhere else.
const RouteFallback = () => (
  <div className="routeSuspenseFallback">
    <SkeletonCardGrid count={6} />
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
      {/* <Route path="/login" element={<LoginPageComponent />} /> */}
      <Route path="/register" element={<RegisterPageComponent />} />

      <Route path="/add-category" element={<AddCategoryPageComponent />} />
      <Route path="/categories" element={<CategoriesListPageComponent />} />
      <Route path="/categories/edit/:categoryId" element={<EditCategoryPageComponent />} />

      <Route path="/add-attribute" element={<AddAttributePageComponent />} />
      <Route path="/attributes" element={<GetAttributesPageComponent />} />
      <Route path="/attributes/edit/:id" element={<EditAttributePageComponent />} />
      <Route path="/attributes/:attributeId/values" element={<AttributeValuesPageComponent />} />

      <Route path="/add-priceList" element={<AddPriceListPageComponent />} />
      <Route path="/priceLists" element={<GetPriceListsPageComponent />} />
      <Route path="/priceLists/edit/:priceListId" element={<EditPriceListPageComponent />} />

      {/* <Route path="/add-product" element={<AddProductPageComponent />} /> */}
      {/* <Route path="/items" element={<getItemsPageComponent />} />
      <Route path="/items/edit/:productId" element={<EditProductPageComponent />} />
      <Route path="/items/:productId/prices" element={<ManageProductPricePageComponent />} />
      <Route path="/items/:productId/images" element={<ManageProductImagesPageComponent />} /> */}

      <Route path="/categories/:categoryId" element={<ItemListPageComponent />} />
      <Route path="/categories/:categoryId/itemGroup/:itemGroupId" element={<ItemListPageComponent />} />
      <Route path="/search" element={<ItemListPageComponent />} />
      <Route path="/items" element={<ItemListPageComponent />} />

      {/* 0026: visual category gallery — additive, separate base from /categories* */}
      <Route path="/gallery" element={<CategoryGalleryPageComponent />} />
      <Route path="/gallery/:categoryId" element={<CategoryGalleryPageComponent />} />

      <Route
        path="admin/dashboard/:userId"
        element={
          <AdminRoute>
            <AdminDashboardPageComponent />
          </AdminRoute>
        }
      />
      <Route
        path="admin/dashboard/:userId/items"
        element={
          <AdminRoute>
            <AdminManageItemPageComponent />
          </AdminRoute>
        }
      />
      <Route
        path="admin/dashboard/:userId/items/order"
        element={
          <AdminRoute>
            <AdminDisplayOrderPageComponent />
          </AdminRoute>
        }
      />
      <Route
        path="admin/dashboard/:userId/catalog"
        element={
          <AdminRoute>
            <AdminCatalogManagementPageComponent />
          </AdminRoute>
        }
      />
      <Route
        path="admin/dashboard/:userId/orders"
        element={
          <AdminRoute>
            <BusinessOrdersTableComponent />
          </AdminRoute>
        }
      />
      <Route
        path="admin/dashboard/:userId/analytics"
        element={
          <AdminRoute>
            <AdminAnalyticsPageComponent />
          </AdminRoute>
        }
      />
      <Route
        path="agent/dashboard/:userId"
        element={
          <AgentRoute>
            <AgentDashboardPageComponent />
          </AgentRoute>
        }
      />
      <Route
        path="agent/dashboard/:userId/orders"
        element={
          <AgentRoute>
            <BusinessOrdersTableComponent />
          </AgentRoute>
        }
      />
      <Route
        path="agent/dashboard/:userId/analytics"
        element={
          <AgentRoute>
            <AgentAnalyticsPageComponent />
          </AgentRoute>
        }
      />
      <Route
        path="business/dashboard/:userId"
        element={
          <BusinessRoute>
            <BusinessDashboardPageComponent />
          </BusinessRoute>
        }
      />
      <Route
        path="business/dashboard/:userId/orders"
        element={
          <BusinessRoute>
            <OrdersHistoryPageComponent />
          </BusinessRoute>
        }
      />

      <Route path="/thank-you" element={<ThankYouPageComponent />} />
      <Route path="/shoppingCart" element={<ShoppingCartPageComponent />} />
      <Route path="/" element={<HomePageComponent />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
